import {RefObject, useRef} from "react";

import {useFormRHF} from "@pg-design/rhf-components-module";
import {IOption} from "@pg-design/select-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {addNotification} from "@/features/notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "@/features/notifications/utils/error_notifications";
import {HomeMatchAmplitudeLocation, HomeMatchAmplitudeTracker} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {useAppDispatch} from "@/store/hooks/use_app_dispatch";

import {usePatchHomeMatchPreferencesMutation} from "../../api/patch_home_match_preferences";
import {HomeMatchFinancingPreference} from "../../constants/preferences/home_match_preferences_values";
import {IHomeMatchSurveyApiRequest} from "../../types/home_match_preferences";
import {
    canContinueHomeMatchPreferencesWizardStep,
    getHomeMatchPreferencesWizardStepPatch,
    IHomeMatchPreferencesWizardSurveyForm
} from "../../utils/get_home_match_preferences_wizard_step_patch";
import {
    getHomeMatchPreferencesWizardResumeStepIndex,
    HomeMatchPreferencesWizardStep,
    HomeMatchPreferencesWizardSurveyStep,
    IHomeMatchPreferencesWizardSurvey,
    isHomeMatchPreferencesWizardParameterStep
} from "../../utils/get_home_match_preferences_wizard_steps";
import {getAppliedSurvey, getSavedSurvey, surveysMatch} from "../../utils/get_home_match_preferences_wizard_survey_snapshot";
import {getRoomsRangeValues, RoomsRangeInput} from "../../utils/home_match_rooms_range";

const {LOCATION} = HomeMatchPreferencesWizardStep;

const toWizardRooms = (rooms: RoomsRangeInput | undefined) => {
    if (rooms == null) return null;

    const range = getRoomsRangeValues(rooms);

    if (range.lower == null && range.upper == null) return null;

    return range;
};

interface IParams {
    activeStep: HomeMatchPreferencesWizardStep | undefined;
    amplitudeLocation: HomeMatchAmplitudeLocation;
    editingStep: HomeMatchPreferencesWizardSurveyStep | null;
    financingPreference: HomeMatchFinancingPreference | null;
    isOpen: boolean;
    onResumeStep: (stepIndex: number) => void;
    preferencesSurveySource: PreferencesSurveySource;
    regionOptions: IOption<number>[];
    saveInFlightRef: RefObject<boolean>;
    steps: HomeMatchPreferencesWizardStep[];
    survey: IHomeMatchPreferencesWizardSurvey | null;
}

export const useHomeMatchPreferencesWizardSurvey = (params: IParams) => {
    const {
        activeStep,
        amplitudeLocation,
        editingStep,
        financingPreference,
        isOpen,
        onResumeStep,
        preferencesSurveySource,
        regionOptions,
        saveInFlightRef,
        steps,
        survey
    } = params;
    const dispatch = useAppDispatch();
    const [patchHomeMatchPreferences, {isLoading: isSaving}] = usePatchHomeMatchPreferencesMutation();
    const savedRegionOptionsRef = useRef<IOption<number>[]>(regionOptions);
    const appliedSurveyRef = useRef(getAppliedSurvey(financingPreference, regionOptions, survey));
    const savedRequestRef = useRef<Omit<IHomeMatchSurveyApiRequest, "source">>(getSavedSurvey(financingPreference, survey));
    const formMethods = useFormRHF<IHomeMatchPreferencesWizardSurveyForm>({
        defaultValues: {
            constructionEndDate: survey?.construction_end_date ?? null,
            financing: financingPreference ?? null,
            price: {lower: survey?.price?.lower ?? 0, upper: survey?.price?.upper ?? 0},
            regions: regionOptions,
            rooms: toWizardRooms(survey?.rooms)
        }
    });
    const form = formMethods.watch();
    const canContinue = Boolean(
        activeStep && isHomeMatchPreferencesWizardParameterStep(activeStep) && canContinueHomeMatchPreferencesWizardStep(activeStep, form)
    );

    // Every save reports the whole saved set, so it is read from the refs that hold the latest saved survey.
    const trackSaved = (savedStep: HomeMatchPreferencesWizardStep, autoMatchEnabled?: boolean) => {
        HomeMatchAmplitudeTracker.preferencesWizardSaved({
            autoMatchEnabled,
            location: amplitudeLocation,
            regionLabels: savedRegionOptionsRef.current.map((region) => region.label),
            saved: savedRequestRef.current,
            step: savedStep
        });
    };

    const resetToSaved = () => {
        const saved = savedRequestRef.current;

        formMethods.reset({
            constructionEndDate: saved.construction_end_date ?? null,
            financing: saved.financing_preference ?? null,
            price: {lower: saved.price?.lower ?? 0, upper: saved.price?.upper ?? 0},
            regions: savedRegionOptionsRef.current,
            rooms: toWizardRooms(saved.rooms)
        });
    };

    const nextSurvey = getAppliedSurvey(financingPreference, regionOptions, survey);
    const canAdoptSurveyProps = !editingStep && !formMethods.formState.isDirty;

    if (!surveysMatch(appliedSurveyRef.current, nextSurvey) && canAdoptSurveyProps) {
        appliedSurveyRef.current = nextSurvey;
        savedRequestRef.current = getSavedSurvey(financingPreference, survey);
        savedRegionOptionsRef.current = regionOptions;

        if (isOpen) {
            onResumeStep(getHomeMatchPreferencesWizardResumeStepIndex(steps, savedRequestRef.current));
            resetToSaved();
        }
    }

    const saveStep = async () => {
        if (saveInFlightRef.current || !activeStep || !isHomeMatchPreferencesWizardParameterStep(activeStep)) return false;

        saveInFlightRef.current = true;

        const body = getHomeMatchPreferencesWizardStepPatch({
            constructionEndDate: form.constructionEndDate,
            financing: form.financing,
            priceUpper: form.price.upper,
            regionOptions: form.regions,
            rooms: form.rooms,
            savedSurvey: savedRequestRef.current,
            source: preferencesSurveySource,
            step: activeStep
        });

        try {
            await patchHomeMatchPreferences(body).unwrap();
            savedRequestRef.current = {
                ...savedRequestRef.current,
                price: body.price,
                regions: body.regions,
                rooms: body.rooms,
                ...(body.financing_preference ? {financing_preference: body.financing_preference} : {}),
                ...("construction_end_date" in body ? {construction_end_date: body.construction_end_date} : {})
            };
            appliedSurveyRef.current = getAppliedSurvey(financingPreference, regionOptions, survey);

            if (activeStep === LOCATION) savedRegionOptionsRef.current = form.regions;

            formMethods.reset(formMethods.getValues());
            trackSaved(activeStep);

            return true;
        } catch {
            dispatch(addNotification(defaultAPIErrorNotification));
            return false;
        } finally {
            saveInFlightRef.current = false;
        }
    };

    return {
        canContinue,
        formMethods,
        isSaving,
        patchSaved: () => patchHomeMatchPreferences({...savedRequestRef.current, source: preferencesSurveySource}).unwrap(),
        resetToSaved,
        saved: {request: savedRequestRef.current, regionOptions: savedRegionOptionsRef.current},
        saveStep,
        trackSaved
    };
};
import {RefObject, useRef} from "react";

import {useFormRHF} from "@pg-design/rhf-components-module";
import {IOption} from "@pg-design/select-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {addNotification} from "@/features/notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "@/features/notifications/utils/error_notifications";
import {HomeMatchAmplitudeLocation, HomeMatchAmplitudeTracker} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {useAppDispatch} from "@/store/hooks/use_app_dispatch";

import {usePatchHomeMatchPreferencesMutation} from "../../api/patch_home_match_preferences";
import {HomeMatchFinancingPreference} from "../../constants/preferences/home_match_preferences_values";
import {IHomeMatchSurveyApiRequest} from "../../types/home_match_preferences";
import {
    canContinueHomeMatchPreferencesWizardStep,
    getHomeMatchPreferencesWizardStepPatch,
    IHomeMatchPreferencesWizardSurveyForm
} from "../../utils/get_home_match_preferences_wizard_step_patch";
import {
    getHomeMatchPreferencesWizardResumeStepIndex,
    HomeMatchPreferencesWizardStep,
    HomeMatchPreferencesWizardSurveyStep,
    IHomeMatchPreferencesWizardSurvey,
    isHomeMatchPreferencesWizardParameterStep
} from "../../utils/get_home_match_preferences_wizard_steps";
import {getAppliedSurvey, getSavedSurvey, surveysMatch} from "../../utils/get_home_match_preferences_wizard_survey_snapshot";
import {getRoomsRangeValues, RoomsRangeInput} from "../../utils/home_match_rooms_range";

const {LOCATION} = HomeMatchPreferencesWizardStep;

const toWizardRooms = (rooms: RoomsRangeInput | undefined) => {
    if (rooms == null) return null;

    const range = getRoomsRangeValues(rooms);

    if (range.lower == null && range.upper == null) return null;

    return range;
};

interface IParams {
    activeStep: HomeMatchPreferencesWizardStep | undefined;
    amplitudeLocation: HomeMatchAmplitudeLocation;
    editingStep: HomeMatchPreferencesWizardSurveyStep | null;
    financingPreference: HomeMatchFinancingPreference | null;
    isOpen: boolean;
    onResumeStep: (stepIndex: number) => void;
    preferencesSurveySource: PreferencesSurveySource;
    regionOptions: IOption<number>[];
    saveInFlightRef: RefObject<boolean>;
    steps: HomeMatchPreferencesWizardStep[];
    survey: IHomeMatchPreferencesWizardSurvey | null;
}

export const useHomeMatchPreferencesWizardSurvey = (params: IParams) => {
    const {
        activeStep,
        amplitudeLocation,
        editingStep,
        financingPreference,
        isOpen,
        onResumeStep,
        preferencesSurveySource,
        regionOptions,
        saveInFlightRef,
        steps,
        survey
    } = params;
    const dispatch = useAppDispatch();
    const [patchHomeMatchPreferences, {isLoading: isSaving}] = usePatchHomeMatchPreferencesMutation();
    const savedRegionOptionsRef = useRef<IOption<number>[]>(regionOptions);
    const appliedSurveyRef = useRef(getAppliedSurvey(financingPreference, regionOptions, survey));
    const savedRequestRef = useRef<Omit<IHomeMatchSurveyApiRequest, "source">>(getSavedSurvey(financingPreference, survey));
    const formMethods = useFormRHF<IHomeMatchPreferencesWizardSurveyForm>({
        defaultValues: {
            constructionEndDate: survey?.construction_end_date ?? null,
            financing: financingPreference ?? null,
            price: {lower: survey?.price?.lower ?? 0, upper: survey?.price?.upper ?? 0},
            regions: regionOptions,
            rooms: toWizardRooms(survey?.rooms)
        }
    });
    const form = formMethods.watch();
    const canContinue = Boolean(
        activeStep && isHomeMatchPreferencesWizardParameterStep(activeStep) && canContinueHomeMatchPreferencesWizardStep(activeStep, form)
    );

    // Every save reports the whole saved set, so it is read from the refs that hold the latest saved survey.
    const trackSaved = (savedStep: HomeMatchPreferencesWizardStep, autoMatchEnabled?: boolean) => {
        HomeMatchAmplitudeTracker.preferencesWizardSaved({
            autoMatchEnabled,
            location: amplitudeLocation,
            regionLabels: savedRegionOptionsRef.current.map((region) => region.label),
            saved: savedRequestRef.current,
            step: savedStep
        });
    };

    const resetToSaved = () => {
        const saved = savedRequestRef.current;

        formMethods.reset({
            constructionEndDate: saved.construction_end_date ?? null,
            financing: saved.financing_preference ?? null,
            price: {lower: saved.price?.lower ?? 0, upper: saved.price?.upper ?? 0},
            regions: savedRegionOptionsRef.current,
            rooms: toWizardRooms(saved.rooms)
        });
    };

    const nextSurvey = getAppliedSurvey(financingPreference, regionOptions, survey);
    const canAdoptSurveyProps = !editingStep && !formMethods.formState.isDirty;

    if (!surveysMatch(appliedSurveyRef.current, nextSurvey) && canAdoptSurveyProps) {
        appliedSurveyRef.current = nextSurvey;
        savedRequestRef.current = getSavedSurvey(financingPreference, survey);
        savedRegionOptionsRef.current = regionOptions;

        if (isOpen) {
            onResumeStep(getHomeMatchPreferencesWizardResumeStepIndex(steps, savedRequestRef.current));
            resetToSaved();
        }
    }

    const saveStep = async () => {
        if (saveInFlightRef.current || !activeStep || !isHomeMatchPreferencesWizardParameterStep(activeStep)) return false;

        saveInFlightRef.current = true;

        const body = getHomeMatchPreferencesWizardStepPatch({
            constructionEndDate: form.constructionEndDate,
            financing: form.financing,
            priceUpper: form.price.upper,
            regionOptions: form.regions,
            rooms: form.rooms,
            savedSurvey: savedRequestRef.current,
            source: preferencesSurveySource,
            step: activeStep
        });

        try {
            await patchHomeMatchPreferences(body).unwrap();
            savedRequestRef.current = {
                ...savedRequestRef.current,
                price: body.price,
                regions: body.regions,
                rooms: body.rooms,
                ...(body.financing_preference ? {financing_preference: body.financing_preference} : {}),
                ...("construction_end_date" in body ? {construction_end_date: body.construction_end_date} : {})
            };
            appliedSurveyRef.current = getAppliedSurvey(financingPreference, regionOptions, survey);

            if (activeStep === LOCATION) savedRegionOptionsRef.current = form.regions;

            formMethods.reset(formMethods.getValues());
            trackSaved(activeStep);

            return true;
        } catch {
            dispatch(addNotification(defaultAPIErrorNotification));
            return false;
        } finally {
            saveInFlightRef.current = false;
        }
    };

    return {
        canContinue,
        formMethods,
        isSaving,
        patchSaved: () => patchHomeMatchPreferences({...savedRequestRef.current, source: preferencesSurveySource}).unwrap(),
        resetToSaved,
        saved: {request: savedRequestRef.current, regionOptions: savedRegionOptionsRef.current},
        saveStep,
        trackSaved
    };
};
